import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FaArrowLeft, FaBox, FaTruck, FaCheck, FaClock, FaPaperclip, FaPaperPlane, FaDownload, FaUser, FaStore, FaMapMarkerAlt, FaCreditCard, FaMoneyCheckAlt, FaExclamationTriangle } from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';

export default function SupplierOrderDetail() {
  const { orderId } = useParams();
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showShipModal, setShowShipModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchOrder();
    fetchMessages();
  }, [orderId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du chargement' : 'Error loading order');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleConfirmOrder = async () => {
    try {
      await axios.put(`${API}/orders/${orderId}/confirm?supplier_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Commande confirmée !' : 'Order confirmed!');
      fetchOrder();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleValidatePayment = async () => {
    try {
      await axios.put(`${API}/orders/${orderId}`, {
        payment_status: 'paid'
      });
      toast.success(i18n.language === 'fr' ? 'Paiement validé !' : 'Payment validated!');
      fetchOrder();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la validation' : 'Validation error');
    }
  };

  const handleShipOrder = async () => {
    try {
      let url = `${API}/orders/${orderId}/ship?supplier_id=${user.id}`;
      if (trackingNumber) url += `&tracking_number=${encodeURIComponent(trackingNumber)}`;
      if (carrier) url += `&carrier=${encodeURIComponent(carrier)}`;
      
      await axios.put(url);
      toast.success(i18n.language === 'fr' ? 'Commande marquée comme expédiée !' : 'Order marked as shipped!');
      setShowShipModal(false);
      fetchOrder();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && files.length === 0) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('sender_id', user.id);
      formData.append('sender_name', user.name);
      formData.append('sender_role', 'supplier');
      formData.append('content', newMessage);
      
      files.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/orders/${orderId}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewMessage('');
      setFiles([]);
      fetchMessages();
      toast.success(i18n.language === 'fr' ? 'Message envoyé' : 'Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur d\'envoi' : 'Error sending');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { label: i18n.language === 'fr' ? 'En attente' : 'Pending', color: 'bg-[#00D4FF]/10 text-orange-700', icon: <FaClock /> },
      confirmed: { label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: <FaCheck /> },
      shipped: { label: i18n.language === 'fr' ? 'Expédiée' : 'Shipped', color: 'bg-purple-100 text-purple-700', icon: <FaTruck /> },
      delivered: { label: i18n.language === 'fr' ? 'Livrée' : 'Delivered', color: 'bg-[#635BFF]/10 text-[#5248d9]', icon: <FaCheck /> }
    };
    return statuses[status] || statuses.pending;
  };

  const getPaymentLabel = (method) => {
    const methods = {
      cash: i18n.language === 'fr' ? 'Espèces' : 'Cash',
      card: i18n.language === 'fr' ? 'Carte bancaire' : 'Credit Card',
      bank_transfer: i18n.language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      delivery: i18n.language === 'fr' ? 'Paiement à la livraison' : 'Payment on Delivery'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p>{i18n.language === 'fr' ? 'Commande introuvable' : 'Order not found'}</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/supplier/orders">
              <Button variant="outline" size="icon">
                <FaArrowLeft />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0A2540]">
                {i18n.language === 'fr' ? 'Commande' : 'Order'} #{order.id.substring(0, 8).toUpperCase()}
              </h1>
              <p className="text-sm text-gray-500">
                {new Date(order.created_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
              </p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full font-medium flex items-center gap-2 ${statusInfo.color}`}>
            {statusInfo.icon}
            {statusInfo.label}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Validation Alert - Show if payment not yet validated */}
            {order.payment_status !== 'paid' && order.payment_method === 'purchase_order' && (
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <FaExclamationTriangle className="text-amber-600 text-xl mt-1" />
                    <div className="flex-1">
                      <h3 className="font-bold text-amber-800">
                        {i18n.language === 'fr' ? '⚠️ Paiement en attente de validation' : '⚠️ Payment awaiting validation'}
                      </h3>
                      <p className="text-sm text-amber-700 mt-1">
                        {i18n.language === 'fr' 
                          ? 'Le client a choisi le paiement par Bon de Commande. Veuillez vérifier le BC joint et valider le paiement avant d\'expédier.'
                          : 'Customer chose Purchase Order payment. Please verify the attached PO and validate payment before shipping.'}
                      </p>
                      <Button 
                        onClick={handleValidatePayment} 
                        className="mt-3 bg-green-600 hover:bg-green-700"
                      >
                        <FaMoneyCheckAlt className="mr-2" />
                        {i18n.language === 'fr' ? 'Valider le paiement' : 'Validate Payment'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Step 1: Validate Payment (for purchase orders) */}
                  {order.payment_method === 'purchase_order' && order.payment_status !== 'paid' && (
                    <Button onClick={handleValidatePayment} className="bg-green-600 hover:bg-green-700">
                      <FaMoneyCheckAlt className="mr-2" />
                      {i18n.language === 'fr' ? 'Valider le paiement' : 'Validate Payment'}
                    </Button>
                  )}
                  
                  {/* Payment validated badge */}
                  {order.payment_status === 'paid' && (
                    <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg">
                      <FaCheck />
                      <span className="font-medium">{i18n.language === 'fr' ? 'Paiement validé' : 'Payment validated'}</span>
                    </div>
                  )}

                  {/* Step 2: Confirm Order */}
                  {(order.status === 'pending' || order.status === 'pending_payment') && (
                    <>
                      {order.payment_method === 'purchase_order' && order.payment_status !== 'paid' ? (
                        <Button disabled className="bg-gray-400 cursor-not-allowed">
                          <FaCheck className="mr-2" />
                          {i18n.language === 'fr' ? 'Confirmer (validez le paiement d\'abord)' : 'Confirm (validate payment first)'}
                        </Button>
                      ) : (
                        <Button onClick={handleConfirmOrder} className="bg-blue-600 hover:bg-blue-700">
                          <FaCheck className="mr-2" />
                          {i18n.language === 'fr' ? 'Confirmer la commande' : 'Confirm Order'}
                        </Button>
                      )}
                    </>
                  )}
                  
                  {/* Step 3: Ship Order */}
                  {order.status === 'confirmed' && (
                    <Button onClick={() => setShowShipModal(true)} className="bg-purple-600 hover:bg-purple-700">
                      <FaTruck className="mr-2" />
                      {i18n.language === 'fr' ? 'Marquer comme expédiée' : 'Mark as Shipped'}
                    </Button>
                  )}
                  
                  {/* Tracking info */}
                  {order.tracking_number && (
                    <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded">
                      <FaTruck className="text-gray-500" />
                      <span className="text-sm">
                        <strong>{order.carrier || 'Tracking'}:</strong> {order.tracking_number}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <FaBox className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Articles commandés' : 'Ordered Items'}
                </h2>
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          {i18n.language === 'fr' ? 'Quantité' : 'Qty'}: {item.quantity} • 
                          {item.type === 'new' ? (i18n.language === 'fr' ? ' Neuf' : ' New') :
                           item.type === 'used' ? (i18n.language === 'fr' ? ' Occasion' : ' Used') : ' Rental'}
                        </p>
                      </div>
                      <p className="font-bold text-[#0A2540]">
                        {formatPrice(item.price * item.quantity, 'EUR')}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-[#0A2540]">{formatPrice(order.total, 'EUR')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Messages Section */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  💬 {i18n.language === 'fr' ? 'Messages' : 'Messages'}
                </h2>
                
                {/* Messages List */}
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto mb-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      {i18n.language === 'fr' ? 'Aucun message. Commencez la conversation!' : 'No messages yet. Start the conversation!'}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map(msg => (
                        <div 
                          key={msg.id} 
                          className={`flex ${msg.sender_role === 'supplier' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.sender_role === 'supplier' 
                              ? 'bg-[#0A2540] text-white' 
                              : 'bg-white border'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              {msg.sender_role === 'supplier' ? <FaStore size={12} /> : <FaUser size={12} />}
                              <span className="text-xs font-medium">{msg.sender_name}</span>
                              <span className="text-xs opacity-70">
                                {new Date(msg.created_at).toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className="text-sm">{msg.content}</p>
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.map(att => (
                                  <a 
                                    key={att.id}
                                    href={att.path.startsWith('/api/') 
                                      ? `${API.replace('/api', '')}${att.path}`
                                      : `${API}${att.path}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 text-xs ${
                                      msg.sender_role === 'supplier' ? 'text-blue-200 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                                    }`}
                                  >
                                    <FaDownload />
                                    {att.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="space-y-3">
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {files.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded text-sm">
                          <FaPaperclip className="text-blue-500" />
                          <span className="truncate max-w-32">{file.name}</span>
                          <button type="button" onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaPaperclip />
                    </Button>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={i18n.language === 'fr' ? 'Votre message...' : 'Your message...'}
                      className="flex-1 min-h-[60px]"
                    />
                    <Button type="submit" disabled={sending} className="bg-[#0A2540] hover:bg-[#003d56]">
                      <FaPaperPlane />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Customer Info */}
          <div className="space-y-6">
            {/* Customer Info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FaUser className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Client' : 'Customer'}
                </h3>
                <div className="space-y-2 text-sm">
                  <p><strong>{order.user_name}</strong></p>
                  <p className="text-gray-600">{order.user_email}</p>
                </div>
              </CardContent>
            </Card>

            {/* Delivery Address */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Livraison' : 'Delivery'}
                </h3>
                <p className="text-sm text-gray-600">{order.delivery_address}</p>
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card className={order.payment_method === 'purchase_order' ? 'ring-2 ring-blue-400 bg-blue-50' : ''}>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FaCreditCard className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Paiement' : 'Payment'}
                </h3>
                
                {/* Payment Method Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg mb-4 ${
                  order.payment_method === 'purchase_order' ? 'bg-blue-100 text-blue-800' :
                  order.payment_method === 'bank_transfer' ? 'bg-purple-100 text-purple-800' :
                  order.payment_method === 'card' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {order.payment_method === 'purchase_order' && '📄'}
                  {order.payment_method === 'bank_transfer' && '🏦'}
                  {order.payment_method === 'card' && '💳'}
                  {order.payment_method === 'cash' && '💵'}
                  <span className="font-medium">{getPaymentLabel(order.payment_method)}</span>
                </div>
                
                {/* Purchase Order Details */}
                {order.payment_method === 'purchase_order' && (
                  <div className="space-y-3 border-t pt-4">
                    {/* PO Number */}
                    {order.purchase_order_number && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          {i18n.language === 'fr' ? 'N° Bon de Commande' : 'PO Number'}
                        </span>
                        <span className="font-bold text-blue-700">{order.purchase_order_number}</span>
                      </div>
                    )}
                    
                    {/* Payment Terms */}
                    {order.payment_terms && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          {i18n.language === 'fr' ? 'Échéance' : 'Terms'}
                        </span>
                        <span className="font-medium">{order.payment_terms} {i18n.language === 'fr' ? 'jours' : 'days'}</span>
                      </div>
                    )}
                    
                    {/* Due Date */}
                    {order.payment_due_date && (
                      <div className="flex justify-between items-center bg-amber-50 p-2 rounded">
                        <span className="text-sm text-gray-600">
                          📅 {i18n.language === 'fr' ? 'Date limite' : 'Due Date'}
                        </span>
                        <span className="font-bold text-amber-700">
                          {new Date(order.payment_due_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: 'numeric', month: 'long', year: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    
                    {/* PO File Attachment */}
                    {order.purchase_order_file && (
                      <div className="border-t pt-3 mt-3">
                        <span className="text-sm text-gray-600 block mb-2">
                          📎 {i18n.language === 'fr' ? 'Bon de commande joint' : 'Attached PO'}
                        </span>
                        <a 
                          href={order.purchase_order_file.startsWith('http') 
                            ? order.purchase_order_file 
                            : `${API.replace('/api', '')}${order.purchase_order_file}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                        >
                          <FaDownload />
                          {i18n.language === 'fr' ? 'Télécharger le BC' : 'Download PO'}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Bank Transfer Info */}
                {order.payment_method === 'bank_transfer' && (
                  <div className="text-sm text-gray-600 mt-2 p-3 bg-purple-50 rounded">
                    <p className="font-medium text-purple-800 mb-1">
                      {i18n.language === 'fr' ? '🏦 Virement en attente' : '🏦 Bank transfer pending'}
                    </p>
                    <p>{i18n.language === 'fr' ? 'Le client doit effectuer le virement.' : 'Customer needs to complete the transfer.'}</p>
                  </div>
                )}
                
                {/* Payment Status */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {i18n.language === 'fr' ? 'Statut paiement' : 'Payment Status'}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      order.payment_status === 'invoiced' ? 'bg-blue-100 text-blue-800' :
                      order.payment_status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {order.payment_status === 'paid' ? (i18n.language === 'fr' ? '✓ Payé' : '✓ Paid') :
                       order.payment_status === 'invoiced' ? (i18n.language === 'fr' ? '📄 Facturé' : '📄 Invoiced') :
                       order.payment_status === 'overdue' ? (i18n.language === 'fr' ? '⚠️ En retard' : '⚠️ Overdue') :
                       (i18n.language === 'fr' ? '⏳ En attente' : '⏳ Pending')}
                    </span>
                  </div>
                </div>
                
                {/* Invoice Number */}
                {order.invoice_number && (
                  <div className="mt-3 flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      {i18n.language === 'fr' ? 'N° Facture' : 'Invoice #'}
                    </span>
                    <span className="font-mono font-bold">{order.invoice_number}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Timeline */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4">📋 {i18n.language === 'fr' ? 'Historique' : 'Timeline'}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#635BFF]"></div>
                    <div>
                      <p className="font-medium">{i18n.language === 'fr' ? 'Commande créée' : 'Order created'}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                      </p>
                    </div>
                  </div>
                  {order.confirmed_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Confirmée' : 'Confirmed'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.confirmed_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                  {order.shipped_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Expédiée' : 'Shipped'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.shipped_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                  {order.delivered_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-[#635BFF]"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Livrée' : 'Delivered'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.delivered_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Ship Modal */}
      {showShipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4">
                {i18n.language === 'fr' ? 'Informations d\'expédition' : 'Shipping Information'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {i18n.language === 'fr' ? 'Transporteur' : 'Carrier'}
                  </label>
                  <Input
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    placeholder="Ex: Chronopost, DHL, La Poste..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {i18n.language === 'fr' ? 'Numéro de suivi' : 'Tracking Number'}
                  </label>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Ex: 1Z999AA10123456784"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowShipModal(false)} className="flex-1">
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button onClick={handleShipOrder} className="flex-1 bg-purple-600 hover:bg-purple-700">
                    {i18n.language === 'fr' ? 'Confirmer l\'expédition' : 'Confirm Shipment'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
